import React from "react";
import { Shield, Mail, Lock, Eye, FileText } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-screen py-20 sm:py-24 lg:py-32 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 via-transparent to-orange-600/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-orange-500/10 rounded-full blur-[120px] animate-pulse" />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-4 py-2 mb-6">
            <Shield className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-orange-300">
              Your Privacy Matters
            </span>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl mb-6">
            Privacy{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              Policy
            </span>
          </h1>
          <p className="text-lg text-slate-400">
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-2xl p-8 lg:p-12">
          <div className="prose prose-invert prose-slate max-w-none">
            {/* Introduction */}
            <div className="mb-8">
              <p className="text-slate-300 leading-relaxed">
                M2 Gas Services ("we," "us," or "our") is committed to
                protecting your privacy. This Privacy Policy explains how we
                collect, use, disclose, and safeguard your information when you
                visit our website and use our services.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <FileText className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  1. Information We Collect
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  We collect information that you provide directly to us when
                  you:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request an appointment or service through our website</li>
                  <li>Contact us via phone, email, or contact forms</li>
                  <li>Subscribe to our communications</li>
                  <li>Interact with our social media pages</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  <strong className="text-white">
                    Information collected may include:
                  </strong>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Name and contact information (email address, phone number,
                    mailing address)
                  </li>
                  <li>Service address and property details</li>
                  <li>
                    Service preferences and appointment scheduling information
                  </li>
                  <li>
                    Payment information (processed securely through third-party
                    processors)
                  </li>
                  <li>Communications with our team</li>
                </ul>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Eye className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  2. How We Use Your Information
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide, maintain, and improve our gas line services</li>
                  <li>
                    Schedule appointments and communicate about your service
                    requests
                  </li>
                  <li>Process payments and maintain records</li>
                  <li>
                    Send you service updates, safety information, and
                    promotional materials (with your consent)
                  </li>
                  <li>
                    Respond to your inquiries and provide customer support
                  </li>
                  <li>
                    Comply with legal obligations and protect our legal rights
                  </li>
                  <li>
                    Detect, prevent, and address technical issues or fraudulent
                    activity
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Lock className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  3. How We Share Your Information
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  We do not sell, trade, or rent your personal information to
                  third parties. We may share your information only in the
                  following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong className="text-white">Service Providers:</strong>{" "}
                    With trusted third-party vendors who assist us in operating
                    our website and conducting our business (e.g., payment
                    processors, scheduling tools)
                  </li>
                  <li>
                    <strong className="text-white">Legal Requirements:</strong>{" "}
                    When required by law, court order, or governmental
                    regulation
                  </li>
                  <li>
                    <strong className="text-white">Business Transfers:</strong>{" "}
                    In connection with a merger, sale, or acquisition of all or
                    part of our business
                  </li>
                  <li>
                    <strong className="text-white">Safety & Protection:</strong>{" "}
                    To protect the rights, property, and safety of M2 Gas
                    Services, our customers, or others
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Shield className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  4. Data Security
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  We implement appropriate technical and organizational security
                  measures to protect your personal information against
                  unauthorized access, alteration, disclosure, or destruction.
                  However, no method of transmission over the internet or
                  electronic storage is 100% secure, and we cannot guarantee
                  absolute security.
                </p>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <FileText className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  5. Your Rights and Choices
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong className="text-white">Access:</strong> Request a
                    copy of the personal information we hold about you
                  </li>
                  <li>
                    <strong className="text-white">Correction:</strong> Request
                    correction of inaccurate or incomplete information
                  </li>
                  <li>
                    <strong className="text-white">Deletion:</strong> Request
                    deletion of your personal information (subject to legal
                    obligations)
                  </li>
                  <li>
                    <strong className="text-white">Opt-Out:</strong> Unsubscribe
                    from marketing communications at any time
                  </li>
                  <li>
                    <strong className="text-white">Data Portability:</strong>{" "}
                    Request a copy of your data in a structured format
                  </li>
                </ul>
                <p className="leading-relaxed mt-4">
                  To exercise these rights, please contact us using the
                  information provided below.
                </p>
              </div>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Eye className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  6. Cookies and Tracking Technologies
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  Our website may use cookies and similar tracking technologies
                  to enhance your experience. You can control cookie settings
                  through your browser preferences. Note that disabling cookies
                  may affect website functionality.
                </p>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <FileText className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  7. Children's Privacy
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  Our services are not directed to children under the age of 13.
                  We do not knowingly collect personal information from children
                  under 13. If you believe we have inadvertently collected such
                  information, please contact us immediately.
                </p>
              </div>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <FileText className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  8. Changes to This Privacy Policy
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  We may update this Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page and updating the "Last Updated" date. We encourage
                  you to review this Privacy Policy periodically.
                </p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="mt-12 p-6 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5 text-orange-400" />
                Contact Us
              </h3>
              <p className="text-slate-300 mb-4">
                If you have questions or concerns about this Privacy Policy or
                our data practices, please contact us:
              </p>
              <div className="space-y-2 text-slate-300">
                <p>
                  <strong className="text-white">M2 Gas Services</strong>
                </p>
                <p>133 Hightower Ct, Mooresville, NC 28117</p>
                <p>
                  <a
                    href="tel:9805406064"
                    className="text-orange-400 hover:text-orange-300"
                  >
                    Phone: (980) 540-6064
                  </a>
                </p>
                <p>
                  <a
                    href="mailto:support@m2gasservices.com"
                    className="text-orange-400 hover:text-orange-300"
                  >
                    Email: support@m2gasservices.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
